/**
 * WorkflowChecklist — reusable readiness card.
 *
 * Consumes the structured payload from any `/workflow/.../readiness`
 * endpoint (currently exam; assignment / quiz / timetable / campaign next).
 * Renders:
 *   - colour-coded steps (green / amber / red / grey)
 *   - completion percentage bar
 *   - smart-error blockers list
 *   - publish button that calls an arbitrary publish endpoint and disables
 *     itself with the human-readable reason when not ready
 *
 * Drop into any admin detail page; the `onAction` callback receives the
 * step's `fix` key so the host page can scroll to / open the right form.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, AlertCircle, Circle, AlertTriangle, Loader2, Rocket } from 'lucide-react';

const STATUS_META = {
  green: { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Done' },
  amber: { icon: AlertTriangle, color: 'text-amber-600',   bg: 'bg-amber-50',   border: 'border-amber-200',   label: 'Warning' },
  red:   { icon: AlertCircle,   color: 'text-rose-600',    bg: 'bg-rose-50',    border: 'border-rose-200',    label: 'Required' },
  grey:  { icon: Circle,        color: 'text-slate-400',   bg: 'bg-slate-50',   border: 'border-slate-200',   label: 'Not started' },
};

export default function WorkflowChecklist({
  data,
  loading = false,
  onAction = () => {},
  onPublish = null,
  publishing = false,
  publishLabel = 'Publish',
  className = '',
}) {
  if (loading || !data) {
    return (
      <Card className={className} data-testid="workflow-checklist-loading">
        <CardContent className="py-8 text-center text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin mx-auto" />
        </CardContent>
      </Card>
    );
  }

  const { steps = [], completion_percent = 0, can_publish, blockers = [], warnings = [] } = data;
  const barColor = can_publish ? 'bg-emerald-500' : completion_percent > 50 ? 'bg-amber-500' : 'bg-rose-500';

  return (
    <Card className={className} data-testid="workflow-checklist">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Rocket className="h-4 w-4 text-indigo-600" />
            Readiness · {completion_percent}%
          </CardTitle>
          {onPublish && (
            <Button
              size="sm"
              disabled={!can_publish || publishing}
              onClick={onPublish}
              data-testid="workflow-publish-btn"
            >
              {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Rocket className="h-3.5 w-3.5 mr-1" />}
              {publishLabel}
            </Button>
          )}
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
          <div
            className={`h-full ${barColor} transition-all`}
            style={{ width: `${completion_percent}%` }}
            data-testid="workflow-progress-bar"
          />
        </div>
      </CardHeader>

      <CardContent className="space-y-2">
        {/* Smart-error summary */}
        {blockers.length > 0 && (
          <div className="rounded border border-rose-200 bg-rose-50 p-2.5 text-sm" data-testid="workflow-blockers">
            <p className="font-medium text-rose-700 mb-1 flex items-center gap-1">
              <AlertCircle className="h-3.5 w-3.5" /> Cannot publish yet
            </p>
            <ul className="list-disc list-inside text-rose-700/90 text-xs space-y-0.5">
              {blockers.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          </div>
        )}
        {can_publish && warnings.length > 0 && (
          <div className="rounded border border-amber-200 bg-amber-50 p-2.5 text-sm" data-testid="workflow-warnings">
            <p className="font-medium text-amber-700 mb-1 flex items-center gap-1">
              <AlertTriangle className="h-3.5 w-3.5" /> Ready, but…
            </p>
            <ul className="list-disc list-inside text-amber-700/90 text-xs space-y-0.5">
              {warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}
        {can_publish && warnings.length === 0 && blockers.length === 0 && (
          <div className="rounded border border-emerald-200 bg-emerald-50 p-2 text-sm text-emerald-700 flex items-center gap-2" data-testid="workflow-ready">
            <CheckCircle2 className="h-4 w-4" />
            Ready to publish.
          </div>
        )}

        {/* Step list */}
        <div className="space-y-1.5 pt-1">
          {steps.map((s) => {
            const meta = STATUS_META[s.status] || STATUS_META.grey;
            const Icon = meta.icon;
            return (
              <div
                key={s.key}
                className={`flex items-start gap-2.5 p-2 rounded border ${meta.border} ${meta.bg}`}
                data-testid={`workflow-step-${s.key}`}
              >
                <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${meta.color}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium">{s.label}</p>
                    {!s.required && <Badge variant="outline" className="text-[10px] py-0 px-1.5">Optional</Badge>}
                  </div>
                  {s.message && <p className="text-xs text-slate-600 mt-0.5">{s.message}</p>}
                  {s.help && s.status !== 'green' && (
                    <p className="text-[11px] text-slate-400 mt-0.5">{s.help}</p>
                  )}
                </div>
                {s.fix && s.status !== 'green' && (
                  <button
                    type="button"
                    onClick={() => onAction(s.fix, s)}
                    className="text-xs text-indigo-600 hover:underline shrink-0"
                    data-testid={`workflow-fix-${s.key}`}
                  >
                    Fix →
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
